import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Stack,
  Box,
  Typography,
  Tooltip,
  useTheme,
} from "investira.react.components";
import { validators, formats } from "investira.sdk";

function getUniqueIds(pData) {
  const xIdsSet = new Set();
  pData.forEach((item) => {
    item.ids.forEach((id) => xIdsSet.add(id));
  });
  return Array.from(xIdsSet);
}

function getCorrelationMatrix(pData, pUniqueIds) {
  // Cria um objeto para lookup rápido
  const xLookup = {};
  pData.forEach((xItem) => {
    const [id1, id2] = xItem.ids;
    xLookup[`${id1}|${id2}`] = xItem;
    xLookup[`${id2}|${id1}`] = xItem; // matriz simétrica
  });

  // Monta a matriz
  return pUniqueIds.map((pRowId) =>
    pUniqueIds.map((pColId) => {
      if (pRowId === pColId) return { value: 1, n: null }; // Correlação perfeita na diagonal

      const xItem = xLookup[`${pRowId}|${pColId}`];
      return xItem ? { value: xItem.correlacao, n: xItem.n } : null;
    })
  );
}

// Utilities to blend theme colors
function parseColorToRgb(input) {
  if (!input) return { r: 0, g: 0, b: 0 };
  if (input.startsWith("#")) {
    let c = input.slice(1);
    if (c.length === 3)
      c = c
        .split("")
        .map((ch) => ch + ch)
        .join("");
    const num = parseInt(c, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }
  const m = input.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  return { r: 0, g: 0, b: 0 };
}

function mixRgb(a, b, t) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function rgbToCss({ r, g, b }) {
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

// Tamanho padrão; será recalculado de forma responsiva
const DEFAULT_CELL_SIZE = 48;
const MIN_CELL_SIZE = 28;
const MIN_CELL_HEIGHT_SIZE = 28;

function HeatMap({ correlacoes, itens }) {
  const theme = useTheme();
  const safeCorrelacoes = Array.isArray(correlacoes) ? correlacoes : [];
  const uniqueIds = getUniqueIds(safeCorrelacoes);
  const matrix = getCorrelationMatrix(safeCorrelacoes, uniqueIds);

  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState(DEFAULT_CELL_SIZE);

  useEffect(() => {
    if (!containerRef.current) return;

    const calculateSize = () => {
      const totalColumns = (uniqueIds?.length || 0) + 1; // labels + dados
      if (totalColumns <= 0) return;
      const containerWidth = containerRef.current.clientWidth || 0;
      if (containerWidth <= 0) return;
      const rawSize = containerWidth / totalColumns; // sem arredondar para preencher 100%
      const nextSize = Math.max(MIN_CELL_SIZE, rawSize);
      setCellSize(nextSize);
    };

    const resizeObserver = new ResizeObserver(() => {
      calculateSize();
    });
    resizeObserver.observe(containerRef.current);

    // cálculo inicial
    calculateSize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [uniqueIds.length]);

  const headerFontSize = Math.max(
    10,
    Math.min(12, Math.round(cellSize * 0.25))
  );
  const cellFontSize = Math.max(10, Math.min(14, Math.round(cellSize * 0.3)));

  if (safeCorrelacoes.length === 0) {
    return null;
  }

  const dividerColor = theme.palette.divider;
  const headerBg = theme.palette.background.paper;
  const gridBg = theme.palette.background.paper;
  const neutral = theme.palette.background.paper;
  const neg = theme.palette.error.light;
  const pos = theme.palette.primary.main;

  const neutralRgb = parseColorToRgb(neutral);
  const negRgb = parseColorToRgb(neg);
  const posRgb = parseColorToRgb(pos);

  const getCellColor = (value) => {
    if (value === null || value === undefined) return rgbToCss(neutralRgb);
    if (value >= 1) return pos;
    if (value <= -1) return neg;
    if (value > 0)
      return rgbToCss(mixRgb(neutralRgb, posRgb, Math.min(1, value)));
    return rgbToCss(mixRgb(neutralRgb, negRgb, Math.min(1, Math.abs(value))));
  };

  const getCellHeader = (pId, pItens) => {
    const LABEL_SIZE = 14;
    let xLabel = "";
    let xShortLabel = "";

    if (pItens[pId].ativo_seuid) {
      xLabel = pItens[pId].ativo_seuid;
      xShortLabel = xLabel;
    }

    if (pItens[pId].indicador_id) {
      xLabel = pItens[pId].indicador_id;
      xShortLabel = xLabel;
    }

    if (xLabel.length > LABEL_SIZE) {
      xShortLabel = xLabel.slice(0, LABEL_SIZE) + "...";
    }

    return { label: xLabel, "short-label": xShortLabel };
  };

  return (
    <Card>
      <Stack px={2} py={2}>
        <Stack mb={2}>
          <Typography variant={"h6"}>Matriz de Correlação</Typography>
          <Typography variant={"body2"}>
            Coeficientes de correlação entre componentes.
          </Typography>
        </Stack>
        <Box
          ref={containerRef}
          sx={{ overflow: "auto", maxWidth: "100%", width: "100%" }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${
                uniqueIds.length + 1
              }, ${cellSize}px)`,
              background: gridBg,
              width: "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                width: cellSize,
                height: MIN_CELL_HEIGHT_SIZE,
              }}
            />
            {uniqueIds.map((id, colIndex) => (
              <Tooltip
                key={id}
                title={getCellHeader(id, itens).label}
                arrow
                placement="top"
              >
                <Box
                  key={id}
                  sx={{
                    width: cellSize,
                    height: MIN_CELL_HEIGHT_SIZE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: headerFontSize,
                    borderBottom: `1px solid ${dividerColor}`,
                    background: headerBg,
                    textAlign: "center",
                    px: 0.5,
                    wordBreak: "break-all",
                  }}
                >
                  {getCellHeader(id, itens)["short-label"]}
                </Box>
              </Tooltip>
            ))}

            {/* Rows */}
            {uniqueIds.map((rowId, rowIdx) => (
              <React.Fragment key={rowId}>
                {/* Row header */}
                <Tooltip
                  key={rowId}
                  title={getCellHeader(rowId, itens).label}
                  arrow
                  placement="top"
                  followCursor
                >
                  <Box
                    sx={{
                      width: cellSize,
                      height: MIN_CELL_HEIGHT_SIZE,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: headerFontSize,
                      color: (theme) => theme.palette.getContrastText(headerBg),
                      borderBottom:
                        rowIdx === uniqueIds.length - 1
                          ? "none"
                          : `1px solid ${dividerColor}`,
                      background: headerBg,
                      textAlign: "center",
                      px: 0.5,
                    }}
                  >
                    {getCellHeader(rowId, itens)["short-label"]}
                  </Box>
                </Tooltip>
                {uniqueIds.map((colId, colIdx) => {
                  const value = matrix[rowIdx][colIdx].value;
                  const n = matrix[rowIdx][colIdx].n || null;
                  const display =
                    value === null || value === undefined
                      ? "-"
                      : value === 1
                      ? "1"
                      : value === -1
                      ? "-1"
                      : formats.formatNumber(value, 5);
                  return (
                    <Tooltip
                      key={colId}
                      title={
                        value === null || value === undefined ? (
                          "Sem dado"
                        ) : (
                          <>
                            <Typography
                              color="textPrimary"
                              variant="caption"
                              component="p"
                            >
                              {`Correlação: ${formats.formatNumber(value, 6)}`}
                            </Typography>
                            {n && (
                              <Typography
                                color="textPrimary"
                                variant="caption"
                                component="p"
                              >
                                {`Dias: ${n}`}
                              </Typography>
                            )}
                          </>
                        )
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          width: cellSize,
                          height: MIN_CELL_HEIGHT_SIZE,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom:
                            rowIdx === uniqueIds.length - 1
                              ? "none"
                              : `1px solid ${dividerColor}`,
                          background: getCellColor(value),
                          color: validators.isNull(value)
                            ? theme.palette.text.secondary
                            : (theme) =>
                                theme.palette.getContrastText(
                                  getCellColor(value)
                                ),
                          fontWeight: "bold",
                          fontSize: cellFontSize,
                          cursor:
                            value === null || value === undefined
                              ? "not-allowed"
                              : "pointer",
                          transition: "background 0.2s",
                        }}
                      >
                        {display}
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Stack direction="row" gap={2} alignItems="center" mt={2}>
          <Box
            sx={{
              height: 16,
              width: 16,
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            <Typography variant="caption">-1</Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 16,
              background: `linear-gradient(to right, ${getCellColor(
                -1
              )}, ${getCellColor(0)}, ${getCellColor(1)})`,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              height: 16,
              width: 16,
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            <Typography variant="caption">1</Typography>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}

export default HeatMap;
